import { motion } from "framer-motion";
import { Instagram } from "lucide-react";

const TEAM = [
  {
    id: "lilly",
    name: "Lilly",
    role: "Piercerin",
    studio: "Grevenbroich & Leverkusen",
    instagram: "@piercingsbylillly",
    instagramUrl: "https://instagram.com/piercingsbylillly",
    image: "https://images.unsplash.com/photo-1758600587730-a11917c13b85?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "kenan",
    name: "Kenan",
    role: "Tätowierer",
    studio: "Leverkusen",
    instagram: null,
    instagramUrl: null,
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
  },
  {
    id: "rooney",
    name: "Rooney",
    role: "Tätowierer",
    studio: "Leverkusen",
    instagram: "@tattoorooney",
    instagramUrl: "https://instagram.com/tattoorooney",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
  },
  {
    id: "sascha",
    name: "Sascha",
    role: "Tätowierer",
    studio: "Leverkusen",
    instagram: null,
    instagramUrl: null,
    image: "https://images.unsplash.com/photo-1463453091185-61582044d556?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
  },
  {
    id: "david",
    name: "David",
    role: "Tätowierer",
    studio: "Grevenbroich",
    instagram: "@taghetto",
    instagramUrl: "https://instagram.com/taghetto",
    image: "https://images.unsplash.com/photo-1488161628813-04466f872be2?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
  },
  {
    id: "marcel",
    name: "Marcel",
    role: "Tätowierer",
    studio: "Grevenbroich",
    instagram: "@get_tattooed90",
    instagramUrl: "https://instagram.com/get_tattooed90",
    image: "https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
  },
  {
    id: "tommy",
    name: "Tommy D",
    role: "Tätowierer · Bald wieder da",
    studio: "Demnächst",
    instagram: "@tommydtattoos",
    instagramUrl: "https://instagram.com/tommydtattoos",
    image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
    comingSoon: true,
  },
];

function TeamCard({ member, i }) {
  return (
    <motion.div
      key={member.id}
      data-testid={`team-member-${member.id}`}
      className={`group relative bg-[#141414] border border-[#5A4A1A]/30 hover:border-[#C9A84C]/60 transition-all duration-500 hover:shadow-[0_0_30px_rgba(201,168,76,0.12)] overflow-hidden ${
        member.comingSoon ? "opacity-60" : ""
      }`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: member.comingSoon ? 0.6 : 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: i * 0.08, duration: 0.6 }}
    >
      {/* Photo */}
      <div className="relative aspect-square overflow-hidden">
        <img
          src={member.image}
          alt={`${member.name} – ${member.role}`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700 grayscale group-hover:grayscale-0"
          loading="lazy"
        />
        {member.comingSoon && (
          <div className="absolute inset-0 bg-[#0A0A0A]/60 flex items-center justify-center">
            <span className="text-[#C9A84C] text-xs tracking-[0.2em] uppercase font-outfit border border-[#C9A84C]/40 px-4 py-2">
              Bald wieder da
            </span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-transparent to-transparent" />
      </div>

      {/* Info */}
      <div className="p-5">
        <h3 className="font-cormorant text-xl text-[#F0F0F0] font-semibold">{member.name}</h3>
        <p className="text-[#888888] text-xs font-outfit mt-0.5">{member.role}</p>
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-[#5A4A1A] font-outfit tracking-wide border border-[#5A4A1A]/40 px-2 py-0.5">
            {member.studio}
          </span>
          {member.instagram && (
            <a
              href={member.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`team-instagram-${member.id}`}
              className="text-[#888888] hover:text-[#C9A84C] transition-colors"
            >
              <Instagram size={16} />
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function TeamSection() {
  return (
    <section
      id="team"
      data-testid="team-section"
      className="py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <span className="section-label">Das Team</span>
          <h2 className="section-heading">Unsere Künstler.</h2>
        </motion.div>

        {/* Franco – Featured Card */}
        <motion.div
          data-testid="team-member-franco"
          className="group relative bg-[#141414] border border-[#5A4A1A]/40 hover:border-[#C9A84C]/60 transition-all duration-500 hover:shadow-[0_0_40px_rgba(201,168,76,0.12)] overflow-hidden mb-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="grid md:grid-cols-2 gap-0">
            {/* Image */}
            <div className="relative h-72 md:h-auto overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1765776830139-72b2184dae5a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
                alt="Franco – Inhaber Royal Tattoo"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#141414]/80 md:block hidden" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#141414] to-transparent md:hidden" />
            </div>

            {/* Content */}
            <div className="p-8 md:p-12 flex flex-col justify-center">
              <span className="text-[#C9A84C] text-xs tracking-[0.3em] uppercase font-outfit mb-4">
                Inhaber & Studioleitung
              </span>
              <h3 className="font-cormorant text-4xl md:text-5xl text-[#F0F0F0] mb-2">
                Franco
              </h3>
              <p className="text-[#888888] text-sm font-outfit mb-6">
                Francesco Chiaromonte
              </p>
              <p className="text-[#888888] text-sm font-outfit leading-relaxed mb-4">
                Beide Standorte · Grevenbroich & Leverkusen
              </p>
              <p className="text-[#F0F0F0] font-cormorant text-xl italic mb-8 leading-relaxed">
                "Haut und Kunst müssen keine Gegensätze sein."
              </p>
              <p className="text-[#888888] text-sm font-outfit leading-relaxed mb-8">
                Franco gründete Royal Tattoo mit einer klaren Vision: ein Studio, das höchste
                künstlerische Qualität mit echtem Handwerk verbindet. Heute leitet er zwei Standorte
                und zieht internationale Talente an — weil Royal nur das Beste ist.
              </p>
              <a
                href="https://instagram.com/royal.tattoo.downtown"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="team-instagram-franco"
                className="flex items-center gap-2 text-[#888888] hover:text-[#C9A84C] transition-colors w-fit"
              >
                <Instagram size={16} />
                <span className="text-sm font-outfit">@royal.tattoo.downtown</span>
              </a>
            </div>
          </div>
        </motion.div>

        {/* Team Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {TEAM.map((member, i) => (
            <TeamCard key={member.id} member={member} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
